import { useCallback, useEffect, useMemo } from 'react';

import type { FormInstance } from '@arco-design/web-react';
import { Form } from '@arco-design/web-react';

import { useSceneLayoutContext } from '../SceneLayoutContext';
import { type ResourceEditMode, SKU_RESOURCE_MODULES_FIELD, type SceneLayoutDeployTypeCode, type SkuResourceModulesState } from '../types';
import {
  buildDefaultDirectResourceModules,
  buildDefaultSkuResourceModules,
  getDirectResourceModuleKeys,
  getOrderResourceModuleKeys,
  getSkuResourceModuleKeys,
  resolveLockedModuleKeys,
} from '../utils/resolveModuleDefaults';

function isSkuResourceModuleEnabled(
  modules: SkuResourceModulesState | undefined,
  moduleKey: string,
  options?: { moduleSelectionEnabled?: boolean; defaultEnabled?: boolean },
): boolean {
  if (!options?.moduleSelectionEnabled) return true;
  if (!modules || typeof modules !== 'object') {
    return options.defaultEnabled ?? false;
  }
  if (moduleKey in modules) return !!modules[moduleKey];
  return true;
}

/** 从直建默认态裁剪为下单可见模块勾选 */
function buildDefaultOrderResourceModules(deployTypeCode: SceneLayoutDeployTypeCode): SkuResourceModulesState {
  const defaults = buildDefaultDirectResourceModules(deployTypeCode);
  return Object.fromEntries(getOrderResourceModuleKeys(deployTypeCode).map((key) => [key, defaults[key] === true]));
}

export interface UseSkuModuleSelectionOptions {
  innerForm: FormInstance;
  deployTypeCode: SceneLayoutDeployTypeCode;
  skuType?: string;
  paymentTiming?: string;
  moduleSelectionEnabled?: boolean;
  resetModuleFields?: (moduleKey: string) => void;
  /** Legacy ResourceFormProvider 未包 SceneLayoutProvider 时显式传入 */
  resourceEditMode?: ResourceEditMode;
  /** 直建合并「资源配置」步：模块勾选与 SKU 创建 UI 一致 */
  directCreateMode?: boolean;
  /** 运行时追加锁定（如镜像预填存储 / 环境变量后不可取消勾选） */
  extraLockedModuleKeys?: string[];
  /** 下单：SKU preset 中的 skuResourceModules，用于锁定 = 直建锁定 ∪ preset 已勾选 */
  presetSkuResourceModules?: SkuResourceModulesState;
}

export function useSkuModuleSelection({
  innerForm,
  deployTypeCode,
  skuType,
  paymentTiming,
  moduleSelectionEnabled = false,
  resetModuleFields,
  resourceEditMode: resourceEditModeOverride,
  directCreateMode = false,
  extraLockedModuleKeys,
  presetSkuResourceModules,
}: UseSkuModuleSelectionOptions) {
  const scene = useSceneLayoutContext();
  const resourceEditMode = resourceEditModeOverride ?? scene.resourceEditMode;
  const isOrderPostPaid = resourceEditMode === 'order-post-paid';
  const isOrderFixed = resourceEditMode === 'order-fixed';
  const isOrderCustom = resourceEditMode === 'order-custom';
  const isDirectCreate = directCreateMode;
  const isOrderContext = isOrderPostPaid || isOrderFixed || isOrderCustom;
  const modules = Form.useWatch(SKU_RESOURCE_MODULES_FIELD, innerForm) as SkuResourceModulesState | undefined;
  const isFixedSku = skuType === 'fixed';
  const layoutEnabled = moduleSelectionEnabled || isDirectCreate || isOrderContext;
  const moduleKeys = isOrderContext
    ? getOrderResourceModuleKeys(deployTypeCode)
    : isDirectCreate
      ? getDirectResourceModuleKeys(deployTypeCode)
      : getSkuResourceModuleKeys(deployTypeCode, paymentTiming);
  const lockedModuleKeys = useMemo(() => {
    const base = isOrderContext
      ? resolveLockedModuleKeys(deployTypeCode, { orderMode: true, presetSkuResourceModules })
      : isDirectCreate
        ? resolveLockedModuleKeys(deployTypeCode, { directCreateMode: true })
        : resolveLockedModuleKeys(deployTypeCode, { skuType });
    if (!extraLockedModuleKeys?.length) return base;
    return Array.from(new Set([...base, ...extraLockedModuleKeys]));
  }, [deployTypeCode, extraLockedModuleKeys, isDirectCreate, isOrderContext, presetSkuResourceModules, skuType]);
  const moduleKeySignature = moduleKeys.join(',');
  const lockedKeySignature = lockedModuleKeys.join(',');
  /** hydrate 可能覆盖勾选态；依赖变化后重新强制锁定模块 */
  const modulesSignature = modules ? moduleKeys.map((key) => `${key}:${modules[key] === true ? 1 : 0}`).join(',') : '';

  useEffect(() => {
    if (isOrderContext) {
      const current = innerForm.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as SkuResourceModulesState | undefined;
      const defaults = buildDefaultOrderResourceModules(deployTypeCode);
      const next: SkuResourceModulesState = current && typeof current === 'object' ? { ...current } : { ...defaults };
      let changed = !current || typeof current !== 'object';

      moduleKeys.forEach((key) => {
        if (!(key in next)) {
          next[key] = defaults[key] === true;
          changed = true;
        }
      });
      Object.keys(next).forEach((key) => {
        if (!moduleKeys.includes(key)) {
          delete next[key];
          changed = true;
        }
      });
      lockedModuleKeys.forEach((key) => {
        if (!next[key]) {
          next[key] = true;
          changed = true;
        }
      });

      if (changed) {
        innerForm.setFieldValue(SKU_RESOURCE_MODULES_FIELD, next);
      }
      return;
    }

    if (isDirectCreate) {
      const current = innerForm.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as SkuResourceModulesState | undefined;
      const defaults = buildDefaultDirectResourceModules(deployTypeCode);

      if (!current || typeof current !== 'object') {
        innerForm.setFieldValue(SKU_RESOURCE_MODULES_FIELD, defaults);
        return;
      }

      let changed = false;
      const next = { ...current };
      moduleKeys.forEach((key) => {
        if (!(key in next)) {
          next[key] = defaults[key] ?? false;
          changed = true;
        }
      });
      lockedModuleKeys.forEach((key) => {
        if (!next[key]) {
          next[key] = true;
          changed = true;
        }
      });
      Object.keys(next).forEach((key) => {
        if (!moduleKeys.includes(key)) {
          delete next[key];
          changed = true;
        }
      });
      if (changed) {
        innerForm.setFieldValue(SKU_RESOURCE_MODULES_FIELD, next);
      }
      return;
    }

    if (!layoutEnabled || !moduleSelectionEnabled) return;

    const current = innerForm.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as SkuResourceModulesState | undefined;
    const defaults = buildDefaultSkuResourceModules(deployTypeCode, skuType, paymentTiming);

    if (!current || typeof current !== 'object') {
      innerForm.setFieldValue(SKU_RESOURCE_MODULES_FIELD, defaults);
      return;
    }

    let changed = false;
    const next = { ...current };
    moduleKeys.forEach((key) => {
      if (!(key in next)) {
        next[key] = defaults[key];
        changed = true;
      }
    });
    Object.keys(next).forEach((key) => {
      if (!moduleKeys.includes(key)) {
        delete next[key];
        changed = true;
      }
    });
    lockedModuleKeys.forEach((key) => {
      if (!next[key]) {
        next[key] = true;
        changed = true;
      }
    });
    if (changed) {
      innerForm.setFieldValue(SKU_RESOURCE_MODULES_FIELD, next);
    }
  }, [
    deployTypeCode,
    innerForm,
    isDirectCreate,
    isOrderContext,
    layoutEnabled,
    lockedKeySignature,
    lockedModuleKeys,
    moduleKeySignature,
    moduleKeys,
    moduleSelectionEnabled,
    modulesSignature,
    paymentTiming,
    skuType,
  ]);

  const setModuleEnabled = useCallback(
    (moduleKey: string, checked: boolean) => {
      if (lockedModuleKeys.includes(moduleKey)) return;
      if (!isDirectCreate && !isOrderContext && !moduleSelectionEnabled) return;
      const current = (innerForm.getFieldValue(SKU_RESOURCE_MODULES_FIELD) || {}) as SkuResourceModulesState;
      if (!checked) {
        resetModuleFields?.(moduleKey);
      }
      innerForm.setFieldValue(SKU_RESOURCE_MODULES_FIELD, { ...current, [moduleKey]: checked });
    },
    [innerForm, isDirectCreate, isOrderContext, lockedModuleKeys, moduleSelectionEnabled, resetModuleFields],
  );

  const isModuleCheckboxDisabled = useCallback((moduleKey: string) => lockedModuleKeys.includes(moduleKey), [lockedModuleKeys]);

  const isModuleEnabled = useCallback(
    (moduleKey: string) => {
      if (isOrderContext || isDirectCreate) {
        return isSkuResourceModuleEnabled(modules, moduleKey, {
          moduleSelectionEnabled: true,
          defaultEnabled: lockedModuleKeys.includes(moduleKey),
        });
      }
      return isSkuResourceModuleEnabled(modules, moduleKey, {
        moduleSelectionEnabled: layoutEnabled,
        defaultEnabled: isFixedSku,
      });
    },
    [isDirectCreate, isFixedSku, isOrderContext, layoutEnabled, lockedModuleKeys, modules],
  );

  return {
    isFixedSku,
    lockedModuleKeys,
    moduleSelectionEnabled: layoutEnabled,
    showModuleCheckbox: moduleSelectionEnabled || isDirectCreate || isOrderContext,
    isModuleCheckboxDisabled,
    setModuleEnabled,
    isModuleEnabled,
  };
}

export function resolvePreviewModuleKeys(
  innerForm: FormInstance,
  _deployTypeCode: SceneLayoutDeployTypeCode,
  allModuleKeys: readonly string[],
): string[] {
  const modules = innerForm.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as SkuResourceModulesState | undefined;
  return allModuleKeys.filter((key) => modules?.[key] === true);
}
